import { FC, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import WarningIcon from '@mui/icons-material/Warning'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { lgpdService, type Incidente, type SeveridadeIncidente } from '@/services/lgpdService'
import { documentoService } from '@/services/documentoService'
import { CATEGORIAS_INCIDENTE } from '@/types/incidente'
import { tokens } from '@/theme'

const MAX_ANEXOS = 10

/** Referência única do reporte no originei-documentos, gerada antes do protocolo existir. */
const novaReferencia = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `inc-${Date.now()}-${Math.random().toString(16).slice(2)}`

interface Anexo {
  documentoId: string
  nome: string
}

const DPO_EMAIL = (import.meta.env.VITE_DPO_EMAIL as string | undefined) ?? 'dpo@originei.com.br'

const SEVERIDADES: { value: SeveridadeIncidente; label: string }[] = [
  { value: 'BAIXA', label: 'Baixa — sem dado pessoal envolvido' },
  { value: 'MEDIA', label: 'Média — dado pessoal de poucos titulares' },
  { value: 'ALTA', label: 'Alta — vazamento confirmado / muitos titulares' },
  { value: 'CRITICA', label: 'Crítica — vazamento massivo / ataque ativo' },
]

/**
 * Reporte de incidente (LGPD Art. 48).
 *
 * O reporte é **gravado no servidor** e devolve protocolo. Até 15/08 esta tela montava um
 * `mailto:` e entregava o incidente ao cliente de e-mail do usuário — quem não tivesse um
 * configurado simplesmente não reportava, e não sobrava registro nem do incidente nem da
 * tentativa, numa tela que promete comunicação à ANPD em 72 horas.
 */
const IncidentePage: FC = () => {
  const [severidade, setSeveridade] = useState<SeveridadeIncidente>('MEDIA')
  const [categoria, setCategoria] = useState('')
  const [anexos, setAnexos] = useState<Anexo[]>([])
  const [anexando, setAnexando] = useState(false)
  const referencia = useRef(novaReferencia())
  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [titularesAfetados, setTitularesAfetados] = useState('')
  const [acoesTomadas, setAcoesTomadas] = useState('')
  const [contato, setContato] = useState('')

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [registrado, setRegistrado] = useState<Incidente | null>(null)

  const podeEnviar = !!titulo.trim() && !!descricao.trim() && !enviando && !anexando

  const anexar = async (arquivos: FileList | null) => {
    if (!arquivos || arquivos.length === 0) return
    setAnexando(true)
    setErro(null)
    try {
      const enviados: Anexo[] = []
      for (const arquivo of Array.from(arquivos).slice(0, MAX_ANEXOS - anexos.length)) {
        const doc = await documentoService.upload(arquivo, 'OUTRO', referencia.current, 'INCIDENTE')
        enviados.push({ documentoId: doc.id, nome: arquivo.name })
      }
      setAnexos((atuais) => [...atuais, ...enviados])
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Não foi possível anexar o arquivo.')
    } finally {
      setAnexando(false)
    }
  }

  const enviar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      const criado = await lgpdService.reportarIncidente({
        severidade,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        titularesAfetados: titularesAfetados.trim() || undefined,
        acoesTomadas: acoesTomadas.trim() || undefined,
        contato: contato.trim() || undefined,
        categoria: categoria || undefined,
        anexos: anexos.length ? anexos.map((a) => a.documentoId) : undefined,
      })
      setRegistrado(criado)
    } catch (e) {
      const msg = (e as { message?: string })?.message
      setErro(msg ?? 'Não foi possível registrar o incidente. Tente novamente.')
    } finally {
      setEnviando(false)
    }
  }

  if (registrado) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Incidente registrado</AlertTitle>
          Protocolo <strong>{registrado.protocolo}</strong>. O DPO foi acionado e o prazo do
          Art. 48 passa a contar a partir de agora. Guarde o protocolo para acompanhar.
        </Alert>
        <Card>
          <CardHeader
            title={registrado.titulo}
            subheader={`Registrado em ${new Date(registrado.criadoEm).toLocaleString('pt-BR')}`}
            action={<Chip label={registrado.severidade} color="warning" size="small" />}
          />
          <CardContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {registrado.descricao}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => setRegistrado(null)}>
                Reportar outro incidente
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <WarningIcon sx={{ fontSize: 36, color: tokens.statusWarning }} />
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Reporte de Incidente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suspeita ou confirmação de incidente envolvendo dados pessoais
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Em caso de <strong>vazamento confirmado</strong>, a Originei tem até{' '}
        <strong>72 horas</strong> para notificar a ANPD (Art. 48 LGPD). Reporte o quanto antes —
        inclusive suspeitas. Em emergência fora do horário comercial, acione também o DPO em{' '}
        <strong>{DPO_EMAIL}</strong>.
      </Alert>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <Card>
        <CardHeader title="Detalhes do incidente" />
        <CardContent>
          <Stack spacing={2}>
            <TextField
              select
              label="Severidade percebida"
              value={severidade}
              onChange={(e) => setSeveridade(e.target.value as SeveridadeIncidente)}
              helperText="É a sua percepção — a classificação formal é do DPO."
              fullWidth
            >
              {SEVERIDADES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Categoria"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              helperText="Ajuda o DPO a triar; escolha a mais próxima."
              fullWidth
            >
              <MenuItem value="">Não sei dizer</MenuItem>
              {CATEGORIAS_INCIDENTE.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Título"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Possível vazamento de e-mails via log do gateway"
              required
              fullWidth
            />

            <TextField
              label="Descrição detalhada"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="O que aconteceu, quando foi descoberto, qual sistema ou módulo foi afetado"
              multiline
              rows={5}
              required
              fullWidth
            />

            <TextField
              label="Titulares afetados (estimativa)"
              value={titularesAfetados}
              onChange={(e) => setTitularesAfetados(e.target.value)}
              placeholder="Quantos? Que tipo de dado foi exposto?"
              multiline
              rows={2}
              fullWidth
            />

            <TextField
              label="Ações já tomadas"
              value={acoesTomadas}
              onChange={(e) => setAcoesTomadas(e.target.value)}
              placeholder="Contenção, contas desabilitadas, sistemas isolados…"
              multiline
              rows={3}
              fullWidth
            />

            <TextField
              label="Contato para retorno (opcional)"
              value={contato}
              onChange={(e) => setContato(e.target.value)}
              helperText="Sua identidade já fica registrada — preencha se quiser retorno por outro canal."
              fullWidth
            />

            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={anexando ? <CircularProgress size={16} /> : <AttachFileIcon />}
                disabled={anexando || anexos.length >= MAX_ANEXOS}
              >
                {anexando ? 'Enviando…' : 'Anexar evidência'}
                <input
                  hidden
                  type="file"
                  multiple
                  data-testid="anexo-input"
                  onChange={(e) => {
                    anexar(e.target.files)
                    e.target.value = ''
                  }}
                />
              </Button>
              <Typography variant="caption" sx={{ display: 'block', color: tokens.textSecondary, mt: 0.5 }}>
                Prints, logs, e-mails. Até {MAX_ANEXOS} arquivos; ficam em quarentena até a análise.
              </Typography>
              {anexos.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {anexos.map((a) => (
                    <Chip
                      key={a.documentoId}
                      label={a.nome}
                      size="small"
                      onDelete={() => setAnexos((atuais) => atuais.filter((x) => x.documentoId !== a.documentoId))}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={enviar}
              disabled={!podeEnviar}
            >
              {enviando ? 'Registrando…' : 'Registrar incidente'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  )
}

export default IncidentePage
